<script setup lang="ts">
import type { NuxtError } from '#app'

const props = defineProps<{
  error: NuxtError
}>()

const statusCode = computed(() => Number(props.error?.status || 500))
const statusMessage = computed(() => {
  const text = String(props.error?.message || '').trim()
  if (text) {
    return text
  }
  if (statusCode.value === 404) {
    return '页面不存在'
  }
  return '页面暂时不可用'
})
const detailMessage = computed(() => String(props.error?.message || '').trim())
const canRetry = computed(() => statusCode.value >= 500)

const goHome = () => clearError({ redirect: '/' })
const retryCurrent = () => clearError()

useHead({
  title: computed(() => `${statusCode.value} - ${statusMessage.value}`)
})
</script>

<template>
  <NuxtLayout name="error">
    <article class="error-card">
      <p class="error-code">{{ statusCode }}</p>
      <h1 class="error-title">{{ statusMessage }}</h1>
      <p v-if="detailMessage" class="error-detail">{{ detailMessage }}</p>
      <div class="error-actions">
        <button type="button" class="btn-primary" @click="goHome">返回首页</button>
        <button v-if="canRetry" type="button" class="btn-secondary" @click="retryCurrent">重试</button>
      </div>
    </article>
  </NuxtLayout>
</template>

<style scoped>
.error-card {
  padding: 44px 32px;
  text-align: center;
}

.error-code {
  margin: 0;
  font-size: clamp(38px, 8vw, 64px);
  font-weight: 800;
  color: var(--brand);
  letter-spacing: 0.06em;
}

.error-title {
  margin-top: 12px;
  font-size: clamp(24px, 4.6vw, 34px);
}

.error-detail {
  max-width: 720px;
  margin: 14px auto 0;
  color: var(--text-soft);
}

.error-actions {
  margin-top: 24px;
  display: inline-flex;
  flex-wrap: wrap;
  gap: 10px;
}

.btn-primary,
.btn-secondary {
  appearance: none;
  border-radius: 10px;
  padding: 10px 16px;
  font-weight: 700;
  font-size: 14px;
  border: 1px solid transparent;
  cursor: pointer;
  transition:
    transform 0.16s ease,
    box-shadow 0.16s ease,
    border-color 0.16s ease,
    background-color 0.16s ease,
    color 0.16s ease;
}

.btn-primary {
  background: var(--brand);
  color: #fff;
}

.btn-secondary {
  border-color: var(--line);
  background: var(--bg-soft);
  color: var(--text);
}

.btn-primary:hover,
.btn-secondary:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow);
}

@media (max-width: 640px) {
  .error-card {
    padding: 32px 20px;
  }
}
</style>
